import { CARD_API_ENDPOINT } from '@/config/consts'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { createSmartFetch, createSmartGet } from './useAuthenticatedMutation'
import { type UserCard, useUserCards } from './useUserCards'

export interface EncryptedSecret {
  data: string
  iv: string
}

export interface CardRevealSecrets {
  encryptedPan: EncryptedSecret
  encryptedCvc: EncryptedSecret
  ExpirationMonth: string
  ExpirationYear: string
}

interface CardSecretsResponse {
  data: CardRevealSecrets
}

interface PublicKeyResponse {
  data: string
}

const fetchPublicKey = createSmartGet(`${CARD_API_ENDPOINT}/v1/vendor/cards/pubkey`)

export async function getCardPublicKey(): Promise<string> {
  const response: PublicKeyResponse = await fetchPublicKey()
  return response.data
}

export async function getCardSecrets(cardID: string, sessionId: string): Promise<CardRevealSecrets> {
  const fetchCardSecrets = createSmartGet(`${CARD_API_ENDPOINT}/v1/vendor/cards/secrets/${cardID}`, {
    headers: {
      sessionID: sessionId,
    },
  })

  const response: CardSecretsResponse = await fetchCardSecrets()
  return response.data
}

export function useCardRevealPayload(cardOverride?: UserCard | null) {
  const { activeCard: defaultCard } = useUserCards()
  const activeCard = cardOverride ?? defaultCard

  return useMutation<CardRevealSecrets, Error, { sessionId: string }>({
    mutationFn: async ({ sessionId }) => {
      if (!activeCard) {
        throw new Error('No active card found')
      }

      if (activeCard.status.toLowerCase() !== 'active') {
        throw new Error(`Cannot fetch card details - card status is ${activeCard.status}`)
      }

      return getCardSecrets(activeCard.cardID, sessionId)
    },
  })
}

export function useUpdateEncryptedPin(cardOverride?: UserCard | null) {
  const queryClient = useQueryClient()
  const { activeCard: defaultCard } = useUserCards()
  const activeCard = cardOverride ?? defaultCard

  const pinUpdateUrl = `${CARD_API_ENDPOINT}/v1/vendor/cards/pin`

  return useMutation<
    { success: boolean; message?: string },
    Error,
    { sessionId: string; encryptedPin: EncryptedSecret }
  >({
    mutationFn: async ({ sessionId, encryptedPin }) => {
      if (!activeCard?.cardID) {
        throw new Error('Missing required data for PIN update')
      }

      const updatePin = createSmartFetch(pinUpdateUrl, {
        method: 'PATCH',
        headers: {
          sessionID: sessionId,
        },
      })

      await updatePin({
        pin: { encryptedPin },
        cardID: activeCard.cardID,
      })

      return { success: true, message: 'PIN updated successfully' }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['cardDetails'] })
    },
  })
}

export function useFreezeCard(cardOverride?: UserCard | null) {
  const queryClient = useQueryClient()
  const { activeCard: defaultCard } = useUserCards()
  const activeCard = cardOverride ?? defaultCard
  const activeCardRef = useRef(activeCard)
  activeCardRef.current = activeCard

  const cardStatusUrl = `${CARD_API_ENDPOINT}/v1/vendor/cards/status`

  return useMutation<{ success: boolean }, Error, boolean>({
    mutationFn: async (shouldFreeze: boolean) => {
      const card = activeCardRef.current
      if (!card) {
        throw new Error('No active card found')
      }

      const requestBody = {
        cardID: card.cardID,
        freeze: shouldFreeze,
      }

      const updateCardStatus = createSmartFetch(cardStatusUrl, {
        method: 'PATCH',
      })

      await updateCardStatus(requestBody)

      return { success: true }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cards'] })
    },
  })
}
